'use client';

import { create } from 'zustand';

import { userApi, type UpdateUserProfilePayload, type UserProfile } from '@/lib/api/users';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';

const USER_PROFILE_ERROR = {
  LOAD_FAILED: 'user_profile_load_failed',
  SAVE_FAILED: 'user_profile_save_failed',
  AVATAR_UPLOAD_FAILED: 'user_profile_avatar_upload_failed',
} as const;

let profileRevision = 0;
let pendingProfile: { userId: string; version: number; revision: number; promise: Promise<UserProfile | null> } | null = null;

function syncAuthAvatar(avatarUrl: string | null) {
  const authUser = useAuthStore.getState().user;
  if (!authUser) return;

  useAuthStore.setState({
    user: {
      ...authUser,
      user_metadata: {
        ...(authUser.user_metadata || {}),
        avatar_url: avatarUrl || undefined,
      },
    },
  });
}

function revokeBlobUrl(url?: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

interface UserProfileState {
  profile: UserProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadedUserId: string | null;
  fetchProfile: (force?: boolean) => Promise<UserProfile | null>;
  applyServerProfile: (profile: UserProfile) => boolean;
  updateProfile: (updates: UpdateUserProfilePayload) => Promise<UserProfile | null>;
  uploadAvatar: (file: File, optimisticUrl?: string | null) => Promise<string | null>;
  clearAvatar: () => Promise<boolean>;
  reset: () => void;
}

export const useUserProfileStore = create<UserProfileState>()((set, get) => ({
  profile: null,
  isLoading: false,
  isSaving: false,
  error: null,
  loadedUserId: null,

  applyServerProfile: (profile) => {
    const authUser = useAuthStore.getState().user;
    if (!authUser?.id || profile.user_id !== authUser.id) return false;
    // A survey/save response is newer than an earlier profile GET.
    profileRevision += 1;
    pendingProfile = null;
    set({
      profile,
      loadedUserId: authUser.id,
      isLoading: false,
      error: null,
    });
    return true;
  },

  fetchProfile: async (force = false) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return null;
    const version = getAuthSessionVersion();
    const revision = profileRevision;
    const isCurrent = () => useAuthStore.getState().user?.id === userId
      && getAuthSessionVersion() === version && profileRevision === revision;
    if (pendingProfile?.userId === userId && pendingProfile.version === version && pendingProfile.revision === revision) {
      return pendingProfile.promise;
    }
    const { loadedUserId, profile } = get();
    if (!force && loadedUserId === userId && profile) return profile;

    const promise = (async () => {
      try {
        set({ isLoading: true, error: null });
        const response = await userApi.getProfile();
        if (!isCurrent()) return null;
        if (response.success === false || response.error || response.data?.user_id !== userId) {
          set({ isLoading: false, error: USER_PROFILE_ERROR.LOAD_FAILED });
          return null;
        }

        const nextProfile = response.data ?? null;
        set({
          profile: nextProfile,
          loadedUserId: userId,
          isLoading: false,
          error: null,
        });
        return nextProfile;
      } catch {
        if (isCurrent()) set({ isLoading: false, error: USER_PROFILE_ERROR.LOAD_FAILED });
        return null;
      }
    })();
    const request = { userId, version, revision, promise };
    pendingProfile = request;
    try {
      return await promise;
    } finally {
      if (pendingProfile === request) pendingProfile = null;
    }
  },

  updateProfile: async (updates) => {
    try {
      set({ isSaving: true, error: null });
      const response = await userApi.updateProfile(updates);
      if (response.error) {
        set({ isSaving: false, error: USER_PROFILE_ERROR.SAVE_FAILED });
        return null;
      }

      const nextProfile = response.data ?? (await get().fetchProfile(true));
      set({ profile: nextProfile, isSaving: false, error: null });
      return nextProfile;
    } catch {
      set({ isSaving: false, error: USER_PROFILE_ERROR.SAVE_FAILED });
      return null;
    }
  },

  uploadAvatar: async (file, optimisticUrl) => {
    const previousProfile = get().profile;
    const previousAvatarUrl = previousProfile?.avatar_url ?? null;

    try {
      set({ isSaving: true, error: null });

      if (optimisticUrl && previousProfile) {
        set({
          profile: {
            ...previousProfile,
            avatar_url: optimisticUrl,
          },
        });
        syncAuthAvatar(optimisticUrl);
      }

      const response = await userApi.uploadAvatar(file);
      if (response.error) {
        if (previousProfile) {
          set({
            profile: {
              ...previousProfile,
              avatar_url: previousAvatarUrl,
            },
          });
        }
        syncAuthAvatar(previousAvatarUrl);
        revokeBlobUrl(optimisticUrl);
        set({ isSaving: false, error: USER_PROFILE_ERROR.AVATAR_UPLOAD_FAILED });
        return null;
      }

      const uploadedUrl = response.data?.url ?? null;

      if (get().profile) {
        set((state) => ({
          profile: state.profile
            ? {
              ...state.profile,
              avatar_url: uploadedUrl,
            }
            : state.profile,
        }));
      } else {
        await get().fetchProfile(true);
      }

      syncAuthAvatar(uploadedUrl);
      revokeBlobUrl(optimisticUrl);
      set({ isSaving: false, error: null });
      return uploadedUrl;
    } catch {
      if (previousProfile) {
        set({
          profile: {
            ...previousProfile,
            avatar_url: previousAvatarUrl,
          },
        });
      }
      syncAuthAvatar(previousAvatarUrl);
      revokeBlobUrl(optimisticUrl);
      set({ isSaving: false, error: USER_PROFILE_ERROR.AVATAR_UPLOAD_FAILED });
      return null;
    }
  },

  clearAvatar: async () => {
    const updated = await get().updateProfile({ avatar_url: null });
    return !!updated;
  },

  reset: () => {
    profileRevision += 1;
    pendingProfile = null;
    set({
      profile: null,
      isLoading: false,
      isSaving: false,
      error: null,
      loadedUserId: null,
    });
  },
}));

registerAuthScopedCacheReset('user-profile', () => useUserProfileStore.getState().reset());
